import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import type { Components } from "react-markdown";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { GradientButton } from "@/components/ui/gradient-button";
import { IntroSvgGradients } from "@/components/ui/intro-svg-gradients";
import "@/components/course/IntroModal.css";

interface IntroModalProps {
  module?: { number: number; of: number };
  title: string;
  subtitle?: string | null;
  body?: string | null;
  ctaLabel?: string;
  onStart: () => void;
}

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2 7L5.5 10.5L12 3.5"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const markdownComponents: Components = {
  ul: ({ children }) => {
    const items = Children.toArray(children).filter(isValidElement);
    return (
      <ul className="intro-modal-objectives">
        {items.map((child, idx) =>
          cloneElement(child as ReactElement<{ style?: CSSProperties }>, {
            style: { "--i": idx } as CSSProperties,
          })
        )}
      </ul>
    );
  },
  li: ({ children, style }) => (
    <li className="intro-modal-objective" style={style}>
      <span className="intro-modal-objective-check" aria-hidden="true">
        <CheckIcon />
      </span>
      <span className="intro-modal-objective-text">{children}</span>
    </li>
  ),
};

export const IntroModal = ({
  module,
  title,
  subtitle,
  body,
  ctaLabel = "Los geht's",
  onStart,
}: IntroModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const titleId = `intro-modal-title-${uid}`;

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleStart = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onStart(), 800);
  };

  return (
    <div
      className={`intro-modal-backdrop ${exiting ? "is-exiting" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className={`intro-modal-card ${mounted ? "is-mounted " : ""}${
          exiting ? "is-exiting-burst" : ""
        }`}
      >
        <header className="intro-modal-hero">
          <div className="intro-modal-hero-ambient" aria-hidden="true" />

          <svg
            className="intro-modal-hero-illustration"
            viewBox="0 0 240 240"
            aria-hidden="true"
          >
            <IntroSvgGradients uid={uid} />

            <circle cx="120" cy="120" r="110" fill={`url(#glow-${uid})`}>
              <animate
                attributeName="r"
                values="100;120;100"
                dur="4s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.7;1;0.7"
                dur="4s"
                repeatCount="indefinite"
              />
            </circle>

            <g transform="translate(120, 120)">
              <g opacity="0.35">
                <ellipse
                  cx="0"
                  cy="-50"
                  rx="78"
                  ry="15"
                  fill="none"
                  stroke={`url(#stroke-${uid})`}
                  strokeWidth="1.5"
                />
                <ellipse
                  cx="0"
                  cy="0"
                  rx="78"
                  ry="15"
                  fill="none"
                  stroke={`url(#stroke-${uid})`}
                  strokeWidth="1.5"
                />
                <ellipse
                  cx="0"
                  cy="50"
                  rx="78"
                  ry="15"
                  fill="none"
                  stroke={`url(#stroke-${uid})`}
                  strokeWidth="1.5"
                />
                <line
                  x1="-78"
                  y1="-50"
                  x2="-78"
                  y2="50"
                  stroke={`url(#stroke-${uid})`}
                  strokeWidth="1.5"
                />
                <line
                  x1="78"
                  y1="-50"
                  x2="78"
                  y2="50"
                  stroke={`url(#stroke-${uid})`}
                  strokeWidth="1.5"
                />
              </g>

              {[
                { cx: 0, r: 3.5, begin: "0s" },
                { cx: -40, r: 3, begin: "0.5s" },
                { cx: 40, r: 3, begin: "1s" },
                { cx: 25, r: 2.5, begin: "1.5s" },
                { cx: -20, r: 2.5, begin: "2s" },
              ].map((p, i) => (
                <circle
                  key={i}
                  className="intro-modal-particle"
                  data-idx={i}
                  cx={p.cx}
                  cy="-50"
                  r={p.r}
                  fill={`url(#particle-${uid})`}
                >
                  <animate
                    attributeName="cy"
                    values="-50;0;50;-50"
                    dur="3s"
                    begin={p.begin}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.1;0.5;0.9;1"
                    dur="3s"
                    begin={p.begin}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
              <circle cx="-78" cy="0" r="2" fill="white" opacity="0.8">
                <animate
                  attributeName="opacity"
                  values="0.2;1;0.2"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="78" cy="0" r="2" fill="white" opacity="0.8">
                <animate
                  attributeName="opacity"
                  values="0.2;1;0.2"
                  dur="2s"
                  begin="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          </svg>

          <div className="intro-modal-hero-content">
            {module && (
              <div className="intro-modal-badge">
                <span className="intro-modal-badge-dot" />
                Modul {module.number} von {module.of}
              </div>
            )}
            <h1 id={titleId} className="intro-modal-title">
              {title}
            </h1>
            <div className="intro-modal-title-underline" />
            {subtitle && <p className="intro-modal-subtitle">{subtitle}</p>}
          </div>
        </header>

        <div className="intro-modal-body">
          <div className="intro-modal-prose">
            {body && (
              <CourseMarkdown components={markdownComponents}>
                {body}
              </CourseMarkdown>
            )}
          </div>

          <footer className="intro-modal-footer">
            <GradientButton onClick={handleStart} disabled={exiting}>
              {ctaLabel}
            </GradientButton>
          </footer>
        </div>
      </div>
    </div>
  );
};
